import React, { createContext, useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { AUDIO_CONFIG, STORAGE_KEYS } from '../config/constants';

// Create the context
const AppContext = createContext(undefined);

// Custom hook to use the context
export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};

/**
 * AppProvider component that wraps the entire application
 * Manages global state for audio, video, favorites, and UI preferences
 */
export const AppProvider = ({ children }) => {
  // Audio state management
  const [binauralAudioState, setBinauralAudioState] = useState({
    stopFunction: null,
    gainNode: null,
    panNodes: null,
    activePresetId: null,
    volume: AUDIO_CONFIG.DEFAULT_BINAURAL_VOLUME,
    panWidth: AUDIO_CONFIG.DEFAULT_PAN_WIDTH,
  });
  
  const [noiseAudioState, setNoiseAudioState] = useState({
    stopFunction: null,
    gainNode: null,
    activePresetId: null,
    volume: AUDIO_CONFIG.DEFAULT_NOISE_VOLUME,
  });
  
  // Video state management
  const [videoState, setVideoState] = useState({
    url: "",
    id: null,
    title: "",
    volume: AUDIO_CONFIG.DEFAULT_VIDEO_VOLUME,
    player: null,
  });
  
  // UI state management
  const [favorites, setFavorites] = useState([]);
  const [darkMode, setDarkMode] = useState(false);
  const [ytApiReady, setYtApiReady] = useState(false);
  const [carrierFrequency, setCarrierFrequency] = useState(AUDIO_CONFIG.DEFAULT_CARRIER_FREQUENCY);

  // Load favorites and user preferences from localStorage on mount
  useEffect(() => {
    try {
      const storedFavorites = localStorage.getItem(STORAGE_KEYS.FAVORITES);
      if (storedFavorites) {
        setFavorites(JSON.parse(storedFavorites));
      }
      
      const storedDarkMode = localStorage.getItem(STORAGE_KEYS.DARK_MODE);
      if (storedDarkMode) {
        setDarkMode(JSON.parse(storedDarkMode));
      }
    } catch (error) {
      console.error('Error loading from localStorage:', error);
    }
  }, []);

  // Save favorites to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.FAVORITES, JSON.stringify(favorites));
    } catch (error) {
      console.error('Error saving favorites to localStorage:', error);
    }
  }, [favorites]);

  // Apply dark mode and save preference
  useEffect(() => {
    try {
      if (darkMode) {
        document.body.classList.add('dark-mode');
      } else {
        document.body.classList.remove('dark-mode');
      }
      localStorage.setItem(STORAGE_KEYS.DARK_MODE, JSON.stringify(darkMode));
    } catch (error) {
      console.error('Error applying dark mode:', error);
    }
  }, [darkMode]);

  // Favorites management functions
  const addToFavorites = (favorite) => {
    if (favorite.id && !favorites.find(fav => fav.id === favorite.id)) {
      setFavorites([...favorites, favorite]);
    }
  };

  const removeFromFavorites = (id) => {
    setFavorites(favorites.filter(fav => fav.id !== id));
  };

  const loadFavorite = (favorite) => {
    setVideoState(prev => ({
      ...prev,
      url: favorite.url,
      id: favorite.id,
      title: favorite.title
    }));
  };

  // Toggle dark mode
  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  // Stop all audio
  const stopAll = () => {
    if (binauralAudioState.stopFunction) {
      binauralAudioState.stopFunction();
      setBinauralAudioState(prev => ({
        ...prev,
        stopFunction: null,
        gainNode: null,
        panNodes: null,
        oscillators: null,
        beatFrequency: null,
        activePresetId: null
      }));
    }
    
    if (noiseAudioState.stopFunction) {
      noiseAudioState.stopFunction();
      setNoiseAudioState(prev => ({
        ...prev,
        stopFunction: null,
        gainNode: null,
        activePresetId: null
      }));
    }
    
    if (videoState.player && videoState.player.stopVideo) {
      videoState.player.stopVideo();
    }
  };

  const value = {
    // Audio states
    binauralAudioState,
    setBinauralAudioState,
    noiseAudioState,
    setNoiseAudioState,
    
    // Video state
    videoState,
    setVideoState,
    
    // UI state
    favorites,
    setFavorites,
    darkMode,
    setDarkMode,
    ytApiReady,
    setYtApiReady,
    carrierFrequency,
    setCarrierFrequency,
    
    // Functions
    addToFavorites,
    removeFromFavorites,
    loadFavorite,
    toggleDarkMode,
    stopAll
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

AppProvider.propTypes = {
  children: PropTypes.node.isRequired
};
